/**
 * O sino de notificações que a pessoa pode tirar da tela no celular: segura o sino, arrasta até
 * a lixeira e solta. Ele fica fixo sobre o conteúdo e, em algumas páginas, atrapalha a leitura.
 *
 * A remoção vale para este navegador (localStorage) e tira só o sino: o convite periódico para
 * ativar as notificações (ModalNotification) continua.
 */

export const CHAVE_DO_SINO_REMOVIDO = "bellaSinoRemovido";

/** Segurar por este tempo é o que separa "quero mover o sino" de um toque ou de rolar a página. */
export const TEMPO_PARA_SEGURAR_MS = 400;

/** O dedo que anda mais que isto antes do tempo está rolando a página, não segurando o sino. */
export const TOLERANCIA_DO_TOQUE_PX = 10;

/** Soltar o sino com o centro dele a esta distância do centro da lixeira o remove. */
export const RAIO_DA_LIXEIRA_PX = 70;

export interface Ponto {
  x: number;
  y: number;
}

type Armazenamento = Pick<Storage, "getItem" | "setItem">;

export function sinoFoiRemovido(armazenamento: Armazenamento): boolean {
  try {
    return armazenamento.getItem(CHAVE_DO_SINO_REMOVIDO) === "1";
  } catch (_e) {
    return false;
  }
}

export function marcarSinoRemovido(armazenamento: Armazenamento): void {
  try {
    armazenamento.setItem(CHAVE_DO_SINO_REMOVIDO, "1");
  } catch (_e) {
    // Sem acesso ao storage: o sino some só até a próxima página
  }
}

export function pontosProximos(a: Ponto, b: Ponto, raio: number): boolean {
  return Math.hypot(a.x - b.x, a.y - b.y) <= raio;
}

export interface GestoDoSino {
  fase: "parado" | "segurando" | "arrastando";
  /** Onde o dedo encostou. */
  inicio: Ponto;
  /** Centro do sino quando o dedo encostou. */
  centroInicial: Ponto;
  /** Quanto o sino já andou desde então. */
  deslocamento: Ponto;
}

const ORIGEM: Ponto = { x: 0, y: 0 };

export const GESTO_PARADO: GestoDoSino = {
  fase: "parado",
  inicio: ORIGEM,
  centroInicial: ORIGEM,
  deslocamento: ORIGEM,
};

export function encostarNoSino(toque: Ponto, centroDoSino: Ponto): GestoDoSino {
  return { fase: "segurando", inicio: toque, centroInicial: centroDoSino, deslocamento: ORIGEM };
}

/** O tempo de segurar passou: se o dedo continua lá, o sino passa a acompanhá-lo. */
export function segurouOSino(gesto: GestoDoSino): GestoDoSino {
  return gesto.fase === "segurando" ? { ...gesto, fase: "arrastando" } : gesto;
}

export function moverDedoNoSino(gesto: GestoDoSino, toque: Ponto): GestoDoSino {
  if (gesto.fase === "segurando") {
    return pontosProximos(gesto.inicio, toque, TOLERANCIA_DO_TOQUE_PX) ? gesto : GESTO_PARADO;
  }
  if (gesto.fase === "arrastando") {
    return {
      ...gesto,
      deslocamento: { x: toque.x - gesto.inicio.x, y: toque.y - gesto.inicio.y },
    };
  }
  return gesto;
}

export function centroAtualDoSino(gesto: GestoDoSino): Ponto {
  return {
    x: gesto.centroInicial.x + gesto.deslocamento.x,
    y: gesto.centroInicial.y + gesto.deslocamento.y,
  };
}
